import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { PageHeader } from '@/components/dashboard/page-header';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_admin/admin/quotas')({
  component: QuotasPage,
});

const PLANS = ['FREE', 'PLUS', 'PREMIUM', 'PRO', 'BUSINESS'] as const;
type PlanValue = (typeof PLANS)[number];

interface Draft {
  initialPerMonth: number;
  repromptPerOffer: number;
  isInitialUnlimited: boolean;
}

function QuotasPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const list = trpc.admin.planQuotaList.useQuery();
  const update = trpc.admin.updatePlanQuota.useMutation({
    onSuccess: () => {
      void utils.admin.planQuotaList.invalidate();
    },
  });
  const [drafts, setDrafts] = React.useState<Record<string, Draft>>({});

  const data = list.data;

  const rowByPlan = new Map((data ?? []).map((r) => [r.plan, r]));

  function draftFor(plan: PlanValue): Draft {
    const existing = drafts[plan];
    if (existing) return existing;
    const row = rowByPlan.get(plan);
    return {
      initialPerMonth: row?.initialPerMonth ?? 0,
      repromptPerOffer: row?.repromptPerOffer ?? 0,
      isInitialUnlimited: row?.isInitialUnlimited ?? false,
    };
  }

  function patch(plan: PlanValue, value: Partial<Draft>): void {
    setDrafts((prev) => ({ ...prev, [plan]: { ...draftFor(plan), ...value } }));
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
      <PageHeader title="Quotas IA" subtitle="Configuration des quotas IA par plan." />
      {list.isLoading ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : !data ? (
        <p className="text-destructive text-sm">Impossible de charger les quotas.</p>
      ) : (
        <div className="space-y-5">
          <p className="text-muted-foreground text-sm">
            Quotas IA par plan. « Générations / mois » = générations initiales de devis ; «
            Re-prompts / offre » = re-prompts par offre. Les modifications sont appliquées
            immédiatement.
          </p>
          {update.error && <p className="text-destructive text-xs">{update.error.message}</p>}
          <div className="space-y-3">
            {PLANS.map((plan) => {
              const draft = draftFor(plan);
              return (
                <div key={plan} className="bg-card border-border rounded-xl border p-5">
                  <div className="flex flex-wrap items-end gap-4">
                    <p className="min-w-24 font-medium">{plan}</p>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted-foreground">Générations / mois</span>
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        className="border-border bg-background w-28 rounded-lg border px-3 py-1.5 text-sm tabular-nums"
                        value={draft.initialPerMonth}
                        onChange={(e) => patch(plan, { initialPerMonth: Number(e.target.value) })}
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="text-muted-foreground">Re-prompts / offre</span>
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        className="border-border bg-background w-28 rounded-lg border px-3 py-1.5 text-sm tabular-nums"
                        value={draft.repromptPerOffer}
                        onChange={(e) => patch(plan, { repromptPerOffer: Number(e.target.value) })}
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={draft.isInitialUnlimited}
                        onChange={(e) => patch(plan, { isInitialUnlimited: e.target.checked })}
                      />
                      <span className="text-muted-foreground">Illimité (cap technique)</span>
                    </label>
                    <button
                      type="button"
                      disabled={update.isPending}
                      onClick={() =>
                        update.mutate({
                          plan,
                          initialPerMonth: draft.initialPerMonth,
                          repromptPerOffer: draft.repromptPerOffer,
                          isInitialUnlimited: draft.isInitialUnlimited,
                        })
                      }
                      className="bg-primary text-primary-foreground ml-auto rounded-lg px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                    >
                      {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
